import React, { useMemo } from 'react';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyField,
  useSlotGroup,
  useTokens,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { contrastRatio } from '../utils/luminance';

// ============================================================================
// Slot group definitions (shared with parent contentSchema)
// ============================================================================

export const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

export const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone',
  fields: {
    text: {
      label: 'Phone',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

export function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Street on line one; "City, ST ZIP" on line two. */
function renderAddressTwoLine(raw: string): React.ReactNode {
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return raw;
  const street = parts.slice(0, -2).join(', ');
  const locality = parts.slice(-2).join(', ');
  return (
    <>
      {street}
      <br />
      <span className="whitespace-nowrap">{locality}</span>
    </>
  );
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '0' && normalized !== 'closed';
}

// Canonicalize any of "9", "9:00", "09:00:00", "9 AM", "9:00am", "18:00"
// into a single display form ("9:00 am") so days that share the same hours
// always compare equal and merge into one range instead of repeating.
// Matches the Footer's office-hours formatting exactly.
export function formatTime(raw: string): string {
  const trimmed = raw.trim();
  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap]m)?$/i);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2] ?? '00';
  const explicitPeriod = match[3]?.toLowerCase();
  const period = explicitPeriod ?? (hours >= 12 ? 'pm' : 'am');
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

// Minutes since midnight, used to order a day's segments (e.g. morning
// before afternoon when a property has a midday/lunch break).
function timeToMinutes(raw: string): number {
  const m = formatTime(raw).match(/^(\d{1,2}):(\d{2}) (am|pm)$/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  const h = parseInt(m[1], 10) % 12;
  return (m[3] === 'pm' ? h + 12 : h) * 60 + parseInt(m[2], 10);
}

/**
 * Groups office hours the same way the Footer does: canonicalizes each
 * segment, always lists all seven weekdays (closed days show "Closed"),
 * keeps midday/lunch breaks as comma-joined segments, and merges consecutive
 * days that share an identical schedule into a single range (e.g. Mon - Fri).
 */
export function groupOfficeHours(raw: OfficeHourEntry[]): GroupedHours[] {
  const byDay = new Map<string, { label: string; segments: { text: string; minutes: number }[] }>();

  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!byDay.has(key)) {
      byDay.set(key, { label: DAY_LABELS[key] || entry.day, segments: [] });
    }
    if (hasValue(entry.open_time) && hasValue(entry.close_time)) {
      const text = `${formatTime(entry.open_time)} - ${formatTime(entry.close_time)}`;
      const day = byDay.get(key)!;
      if (!day.segments.some((s) => s.text === text)) {
        day.segments.push({ text, minutes: timeToMinutes(entry.open_time) });
      }
    }
  }

  const customKeys = [...byDay.keys()].filter((k) => !DAY_ORDER.includes(k));
  const orderedKeys = [...DAY_ORDER, ...customKeys];

  const perDay = orderedKeys.map((key) => {
    const existing = byDay.get(key);
    const label = existing?.label ?? DAY_LABELS[key] ?? key;
    const segments = existing?.segments ?? [];
    const hours = segments.length === 0
      ? 'Closed'
      : segments
          .sort((a, b) => a.minutes - b.minutes)
          .map((s) => s.text)
          .join(', ');
    return { label, hours };
  });

  const groups: GroupedHours[] = [];
  for (const { label, hours } of perDay) {
    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hours) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = startLabel === label ? startLabel : `${startLabel} - ${label}`;
    } else {
      groups.push({ label, hours });
    }
  }

  return groups;
}

// ============================================================================
// Component
// ============================================================================

interface PropertyInfoPanelProps {
  /** Token prefix of the parent component, e.g. "contactform" or "scheduletourform" */
  prefix: string;
}

/**
 * Shared info panel — Visit Us, Call Us, Office Hours cards.
 * Not a registered slot; imported directly by ContactForm and ScheduleTourForm.
 * The parent component owns the contentSchema and provides lint anchors.
 */
export function PropertyInfoPanel({ prefix }: PropertyInfoPanelProps) {
  const visitLabelPath  = `${prefix}.visit_label`;
  const callLabelPath   = `${prefix}.call_label`;
  const hoursLabelPath  = `${prefix}.hours_label`;
  const addressPath     = `${prefix}.address`;
  const phonePath       = `${prefix}.phone`;
  const officeHoursPath = `${prefix}.office_hours`;

  const visitLabel  = useContentToken(visitLabelPath);
  const callLabel   = useContentToken(callLabelPath);
  const hoursLabel  = useContentToken(hoursLabelPath);
  const addressLink = useSlotGroup(addressPath, addressLinkDef);
  const phoneLink   = useSlotGroup(phonePath, phoneLinkDef);

  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as
    | string
    | null
    | undefined;
  const propertyPhone = usePropertyField('property_phone') as
    | string
    | null
    | undefined;

  const officeHours = useCollectionBinding<OfficeHourEntry>(officeHoursPath, {
    collection: 'office_hours',
  });

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay =
    formatDisplayPhone(phoneLink.text) ||
    formatDisplayPhone(callTrackingPhone) ||
    formatDisplayPhone(propertyPhone) ||
    '(555) 555-5555';
  const groupedHours = officeHours.length > 0 ? groupOfficeHours(officeHours) : [];
  const hasHours = groupedHours.length > 0;

  // Underline accent for address/phone CTAs on white cards. When primary is
  // too pale for WCAG AA on the background, fall back to the dark text token
  // so hover feedback stays readable (same picker as header / Apply Now CTAs).
  const tokens = useTokens();
  const linkAccentStyle = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return undefined;
    const accent =
      contrastRatio(primary, background) >= 4.5
        ? primary
        : 'var(--vibe-color-text)';
    return { ['--cf-link-accent' as string]: accent } as React.CSSProperties;
  }, [tokens]);

  return (
    <>
      <div className="cf-info-cards" style={linkAccentStyle}>
        {/* Visit Us — always rendered; property_full_address should always have a value */}
        <div className="cf-card">
          <div className="cf-card-icon">
            <svg width="32" height="32" viewBox="0 0 20 20" fill="none"><path d="M10 1C6.13 1 3 4.13 3 8c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1110 5.5a2.5 2.5 0 010 5z" fill="currentColor"/></svg>
          </div>
          <div className="cf-card-body">
            <EditableToken path={visitLabelPath} as="h3" className="cf-card-label">
              {visitLabel || 'Visit Us'}
            </EditableToken>
            <EditableSlotGroup groupPath={addressPath} definition={addressLinkDef}>
              <a
                href={addressHref}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="cf-card-value cf-card-link"
              >
                {renderAddressTwoLine(addressLink.text || '')}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        <div className="cf-card">
          <div className="cf-card-icon">
            <svg width="32" height="32" viewBox="0 0 20 20" fill="none"><path d="M4 2a2 2 0 00-2 2v1a13 13 0 0013 13h1a2 2 0 002-2v-2.5a1 1 0 00-.68-.95l-3.12-1a1 1 0 00-1.02.27l-1.38 1.38A11 11 0 015.8 7.2l1.38-1.38a1 1 0 00.27-1.02l-1-3.12A1 1 0 005.5 1H4z" fill="currentColor"/></svg>
          </div>
          <div className="cf-card-body">
            <EditableToken path={callLabelPath} as="h3" className="cf-card-label">
              {callLabel || 'Call Us'}
            </EditableToken>
            <EditableSlotGroup groupPath={phonePath} definition={phoneLinkDef}>
              <a
                href={phoneHref || '#'}
                target={phoneLink._target || '_self'}
                className="dniphone dniphonehref cf-card-value cf-card-link"
              >
                {phoneDisplay}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {hasHours && (
          <div className="cf-card cf-card--hours">
            <div className="cf-card-icon">
              <svg width="32" height="32" viewBox="0 0 20 20" fill="none"><path d="M10 0a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm.5-13H9v6l5.25 3.15.75-1.23-4.5-2.67V5z" fill="currentColor"/></svg>
            </div>
            <div className="cf-card-body">
              <EditableToken path={hoursLabelPath} as="h3" className="cf-card-label">
                {hoursLabel || 'Office Hours'}
              </EditableToken>
              <div className="cf-hours-grid">
                {groupedHours.map((group, i) => (
                  <div key={i} className="cf-hours-row">
                    <span className="cf-hours-day">{group.label}</span>
                    <span className="cf-hours-time">
                      {group.hours.split(', ').map((segment, j) => (
                        <span key={j} className="cf-hours-segment">{segment}</span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* ===== Info cards ===== */
        .cf-info-col {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .cf-info-cards {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: stretch;
          gap: 0.75rem;
        }
        .cf-card {
          flex: 1 1 140px;
          display: grid;
          grid-template-rows: 52px auto 1fr;
          justify-items: center;
          align-content: start;
          text-align: center;
          row-gap: 0.4rem;
          padding: 0.75rem;
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }
        .cf-card-icon {
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          background: color-mix(in srgb, var(--vibe-color-secondary) 10%, transparent);
          color: var(--vibe-color-secondary);
        }
        .cf-card-body {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.15rem;
        }
        .cf-card-icon svg {
          width: 30px;
          height: 30px;
        }
        .cf-card-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin: 0 0 0.15rem;
        }
        .cf-card-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-card-link {
          text-decoration: underline;
          text-decoration-color: color-mix(
            in srgb,
            var(--cf-link-accent, var(--vibe-color-text)) 30%,
            transparent
          );
          text-underline-offset: 0.2em;
          text-decoration-thickness: 1.5px;
          color: var(--vibe-color-text);
          font-weight: 500;
          transition: text-decoration-color 0.2s ease, font-weight 0.2s ease;
        }
        .cf-card-link:hover,
        .cf-card-link:focus-visible {
          color: var(--vibe-color-text);
          text-decoration-color: var(--cf-link-accent, var(--vibe-color-text));
          font-weight: 600;
        }

        .cf-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          margin-top: 0.125rem;
          align-self: stretch;
          text-align: left;
        }
        .cf-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 0.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .cf-hours-day {
          color: var(--vibe-color-text);
          font-weight: 500;
          min-width: 5.25rem;
        }
        .cf-hours-time {
          color: var(--vibe-color-text-muted);
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
        .cf-hours-segment {
          display: block;
          white-space: nowrap;
        }

        /* ── Tablet: prevent office-hours rows from wrapping & shrink tile content ── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .cf-card {
            grid-template-rows: 40px auto 1fr;
            row-gap: 0.3rem;
            padding: 0.5rem;
          }
          .cf-card-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
          }
          .cf-card-icon svg {
            width: 22px;
            height: 22px;
          }
          .cf-card-label {
            font-size: 0.8rem;
            letter-spacing: 0.06em;
            margin: 0 0 0.1rem;
          }
          .cf-card-value {
            font-size: 0.85rem;
            line-height: 1.35;
          }
          .cf-hours-row {
            white-space: nowrap;
            font-size: 0.75rem;
            gap: 0.5rem;
          }
          .cf-hours-day {
            min-width: 4.5rem;
          }
        }
      `}</style>
    </>
  );
}
